import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Actions from "../../../components/Common/Actions";
import Breadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import CommonFilterModal from "../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import ListPageLayout from "../../../components/Layout";
import Deletepopup from "../../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";

const DoctorCityList = () => {
  const navigate = useNavigate();
  const addNewCity = {
    id: "addnewpost",
    onClick: () => navigate("/doctors-location-create"),
    style: { width: "240px" },
    label: "Add New City",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  // Filter states
  const [selectedValues, setSelectedValues] = useState({
    locations: [],
    Status: [],
  });
  const [tempSelectedValues, setTempSelectedValues] = useState({
    locations: [],
    Status: [],
  });

  const [selectedCategory, setSelectedCategory] = useState("locations");
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [loader, setLoader] = useState(true);

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}doctor-cities/list`,
      initialParams: {
        sort,
        searchText: search,
        selectedValues,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "doctor-cities",
    fetchData,
    setShowDelete,
  );

  useEffect(() => {
    setLoader(true)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search, selectedValues]);

  const handleActive = (id, status) => {
    setLoader(true)
    axios
      .patch(
        `${process.env.REACT_APP_API_URL}doctor-cities/update/active/${id}`,
        {
          active: !status,
        },
      )
      .then((result) => {
        fetchData();
      });
  };

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Actions
          recordActiveStatus={record.active}
          isView={true}
          isActive={true}
          isEdit={true}
          isDelete={true}
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickView={() => {
            navigate(
              `/doctors-localities/${record.slug_url}?city_id=${record._id}`,
            );
          }}
          onClickEdit={() => navigate(`/doctors-location-edit/${record._id}`)}
          onClickActive={() => handleActive(record._id, record.active)}
        />
      ),
    },
  ];
  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  const filters = {
    locations: ["Chennai", "Coimbatore", "Bangalore", "Salem"],
    Status: ["Active", "Inactive"],
  };

  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Doctor",
        link: "/partnered-doctor-online",
      },
      {
        text: "City / Localities",
        link: "",
      },
    ],
    [],
  );
  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={
        <Breadcrumbs
          className="healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={<FilledButton props={addNewCity} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => setIsFilterModalVisible(true)}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}

      <CommonFilterModal
        visible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
        onApply={() => {
          setSelectedValues(tempSelectedValues);
          setIsFilterModalVisible(false);
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        onReset={() => {
          setTempSelectedValues({ locations: [], Status: [] });
          setSelectedValues({ locations: [], Status: [] });
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        filters={filters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedValues={tempSelectedValues}
        setSelectedValues={setTempSelectedValues}
      />
    </ListPageLayout>
  );
};

export default DoctorCityList;
